import { InputError } from '@backstage/errors';
import { CatalogPayload } from '../../models/CatalogPayload';
import { CatalogResponse } from '../../models/CatalogResponse';
import { CatalogServicePartner } from '../../models/CatalogServicePartner';
import {
  CustomFormFieldLookupResponse,
  CustomFormFieldValidationResponse,
} from '../../models/CustomFormField';
import { FeatureFlag } from '../../models/FeatureFlag';
import { Product } from '../../models/Product';
import { ProductType } from '../../models/ProductType';
import { DataResult } from '../../utils/DataResult';
import { sanitizeExternalUrl } from '../../utils/urls';
import {
  CatalogSnapshot,
  CatalogType,
  CustomFormFieldLookupOptions,
  GetProductsOptions,
  ValidateCustomFormFieldOptions,
} from '../api';
import { BaseApiService, toRequestError } from './http';

const KNOWN_PRODUCT_TYPES: ReadonlySet<number> = new Set(
  Object.values(ProductType).filter(
    (value): value is number => typeof value === 'number',
  ),
);

function isFeatureFlag(value: unknown): value is FeatureFlag {
  const flag = value as FeatureFlag | null;

  return (
    typeof flag === 'object' &&
    flag !== null &&
    typeof flag.name === 'string' &&
    flag.name.length > 0 &&
    typeof flag.enabled === 'boolean'
  );
}

function isRenderableProduct(value: unknown): value is Product {
  const product = value as Product | null;

  return (
    typeof product === 'object' &&
    product !== null &&
    typeof product.id === 'string' &&
    product.id.length > 0 &&
    typeof product.displayName === 'string' &&
    product.displayName.length > 0 &&
    product.showInProducts !== false
  );
}

function sanitizeProduct(product: Product): Product {
  return {
    ...product,
    documentationLink: sanitizeExternalUrl(product.documentationLink),
    slaLink: sanitizeExternalUrl(product.slaLink),
    termsOfUseLink: sanitizeExternalUrl(product.termsOfUseLink),
  };
}

function isNamedServicePartner(value: unknown): value is CatalogServicePartner {
  const servicePartner = value as CatalogServicePartner | null;

  return (
    typeof servicePartner === 'object' &&
    servicePartner !== null &&
    typeof servicePartner.name === 'string' &&
    servicePartner.name.length > 0
  );
}

function isCustomFormFieldLookupResponse(
  value: unknown,
): value is CustomFormFieldLookupResponse {
  const lookup = value as CustomFormFieldLookupResponse | null;

  return (
    typeof lookup === 'object' &&
    lookup !== null &&
    typeof lookup.isValid === 'boolean' &&
    typeof lookup.value === 'object' &&
    lookup.value !== null
  );
}

function isCustomFormFieldValidationResponse(
  value: unknown,
): value is CustomFormFieldValidationResponse {
  const validation = value as CustomFormFieldValidationResponse | null;

  return (
    typeof validation === 'object' &&
    validation !== null &&
    typeof validation.isValid === 'boolean'
  );
}

/**
 * Handles the product catalog: feature flags, products, service partners
 * and the custom form field lookups/validations tied to the catalog.
 */
export class CatalogService extends BaseApiService {
  async getFeatureFlags(withTarget?: boolean): Promise<FeatureFlag[]> {
    return this.fetchArray(
      `/feature${withTarget ? '/targeted' : ''}`,
      isFeatureFlag,
    );
  }

  async getProducts(options: GetProductsOptions): Promise<Product[]> {
    return (await this.getCatalog(options)).products;
  }

  async getCatalog({ type }: GetProductsOptions): Promise<CatalogSnapshot> {
    if (!KNOWN_PRODUCT_TYPES.has(type)) {
      throw new InputError(`Unknown product type '${type}'`);
    }

    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ type: String(type) });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/catalog?${query.toString()}`,
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: (CatalogResponse & CatalogPayload) | null =
      await response.json();
    const products = body?.data?.products ?? body?.products;
    const servicePartners =
      body?.data?.servicePartners ?? body?.servicePartners;

    return {
      products: Array.isArray(products)
        ? products.filter(isRenderableProduct).map(sanitizeProduct)
        : [],
      servicePartners: Array.isArray(servicePartners)
        ? servicePartners.filter(isNamedServicePartner)
        : [],
    };
  }

  async getAverageProvisioningTimes(
    catalogType: CatalogType,
  ): Promise<Record<string, number>> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ type: catalogType });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/provisioning/averageProvisioningTime?${query.toString()}`,
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<Record<string, unknown>> | null =
      await response.json();
    const times = body?.data;

    if (!times || typeof times !== 'object') {
      return {};
    }

    return Object.fromEntries(
      Object.entries(times).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' &&
          Number.isFinite(entry[1]) &&
          entry[1] > 0,
      ),
    );
  }

  async getServicePartners(): Promise<string[]> {
    return this.fetchArray(
      '/catalog/servicepartners',
      (servicePartner): servicePartner is string =>
        typeof servicePartner === 'string',
    );
  }

  async getServicePartnerDetails(
    servicePartnerName: string,
  ): Promise<Record<string, unknown> | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/catalog/servicepartners/${encodeURIComponent(
        servicePartnerName,
      )}`,
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();
    const details = body?.data;

    return typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)
      : undefined;
  }

  async getCustomFormFieldLookup({
    servicePartner,
    customFormFieldId,
    filter,
    signal,
  }: CustomFormFieldLookupOptions): Promise<
    CustomFormFieldLookupResponse | undefined
  > {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams();

    if (filter) {
      query.set('filter', filter);
    }

    const search = query.toString();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/catalog/${encodeURIComponent(
        servicePartner,
      )}/customformfields/${encodeURIComponent(customFormFieldId)}/lookup${
        search ? `?${search}` : ''
      }`,
      { signal },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isCustomFormFieldLookupResponse(body?.data) ? body.data : undefined;
  }

  async validateCustomFormField({
    servicePartner,
    customFormFieldId,
    value,
    filter,
    signal,
  }: ValidateCustomFormFieldOptions): Promise<
    CustomFormFieldValidationResponse | undefined
  > {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams();

    if (filter) {
      query.set('filter', filter);
    }

    const search = query.toString();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/catalog/${encodeURIComponent(
        servicePartner,
      )}/customformfields/${encodeURIComponent(customFormFieldId)}/validate${
        search ? `?${search}` : ''
      }`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
        signal,
      },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isCustomFormFieldValidationResponse(body?.data)
      ? body.data
      : undefined;
  }
}
